import { Dispatch, SetStateAction } from "react";

import { RecordSummarySection } from "../../../../Records/RecordDetailLayout";
import FieldFormatter from "../../../../Records/Formatters/FieldFormatter";
import { ReusableViewIDs } from "../../../../../types/PageInterfaces";
import { Field, Module } from "../../../../../types/TenantInterfaces";
import useImage from "../../../../../api/api-hooks/images/useImage";
import { DetailView } from "../../SharedToolbar/SharedToolbar";
import { AppCardWrapper, CardContainer } from "../CardRecord";
import { CardsLayoutI } from "../../usePageViewTable";

interface Props {
  isSelected: boolean;
  onSelect?: () => void;
  record: Record<string, any>;
  currentModule: Module;
  setOpenRecordData: Dispatch<SetStateAction<DetailView>>;
  imageField: string | null;
  imageFieldPresentation: string | null;
  metadataField: string | null;
  metadataFieldType: string | null;
  setUpdateRecordID: Dispatch<SetStateAction<string | null>>;
  maxHeight?: number;
  viewID: string;
  cardLayout: CardsLayoutI & { id: string };
  frontFieldsSection: RecordSummarySection;
  cardImageField: string | undefined;
  reusableViews?: ReusableViewIDs;
  commentsWidget?: boolean;
  CustomCardFooter?: ({
    record,
  }: {
    record: Record<string, any>;
  }) => React.ReactNode;
}

export default function ImageCardRecord({
  isSelected,
  onSelect,
  record,
  currentModule,
  setOpenRecordData,
  imageField,
  imageFieldPresentation,
  metadataField,
  metadataFieldType,
  setUpdateRecordID,
  viewID,
  frontFieldsSection,
  cardImageField,
  reusableViews,
  commentsWidget,
  CustomCardFooter,
}: Props) {
  return (
    <CardContainer>
      <AppCardWrapper
        isSelected={isSelected}
        onSelect={onSelect}
        record={record}
        currentModule={currentModule}
        setOpenRecordData={setOpenRecordData}
        imageField={imageField}
        imageFieldPresentation={imageFieldPresentation}
        metadataField={metadataField}
        metadataFieldType={metadataFieldType}
        setUpdateRecordID={setUpdateRecordID}
        commentsWidget={commentsWidget}
        renderedSection={
          <FrontImageCard
            frontSection={frontFieldsSection}
            currentModule={currentModule}
            record={record}
            cardImageField={cardImageField}
            reusableViews={reusableViews}
          />
        }
        viewID={viewID}
        reusableViews={reusableViews}
      />
      {CustomCardFooter && <CustomCardFooter record={record} />}
    </CardContainer>
  );
}

const FrontImageCard = ({
  currentModule,
  frontSection,
  record,
  cardImageField,
  reusableViews,
}: {
  currentModule: Module;
  record: Record<string, any>;
  frontSection: RecordSummarySection;
  cardImageField?: string;
  reusableViews?: ReusableViewIDs;
}) => {
  let recordImage = record?.[cardImageField!];

  // Adds support for SingleFile fields.
  // Checks first File in Array, and ensures it's an Image.
  recordImage = Array.isArray(recordImage)
    ? recordImage?.[0]
      ? recordImage?.[0]?.Type?.startsWith("image")
        ? recordImage[0]
        : undefined
      : undefined
    : recordImage;

  const privateFileURL = useImage(
    recordImage?.PublicURL ? "" : recordImage?.File
  );
  const fields = frontSection?.Fields?.map((f) => f.Field as Field) || [];

  const fileURL = recordImage?.PublicURL
    ? recordImage?.PublicURL
    : privateFileURL?.fileURL;

  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:pb-2 tw:relative">
      {fileURL ? (
        <div
          className="tw:w-full"
          style={{ maxWidth: "330px", height: "120px" }}
        >
          <img
            src={fileURL}
            className="tw:w-full tw:h-full tw:rounded tw:object-cover"
            alt=""
          />
        </div>
      ) : (
        <div
          className="tw:rounded tw:w-full tw:bg-empty-state"
          style={{
            maxWidth: "330px",
            height: "120px",
          }}
        >
          <div
            style={{ height: "120px" }}
            className="tw:w-full tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-light"
          >
            <i className="mdi mdi-image-off tw:text-xl" />
            <small className="tw:font-medium">Image Not Found</small>
          </div>
        </div>
      )}
      <div className="tw:flex tw:w-full tw:pt-2 tw:justify-between">
        <div className="tw:w-full tw:flex tw:flex-col tw:gap-1">
          {fields.map((f) => {
            return (
              <div
                className="tw:flex tw:items-center tw:justify-between"
                key={f.APIName}
              >
                <h5 className="tw:mb-0 tw:font-medium tw:text-dark tw:mr-3 tw:text-[13px] tw:text-nowrap">
                  {f.Name}:
                </h5>
                <div className="min-w-[100px] w-full tw:text-[13px] tw:truncate">
                  <FieldFormatter
                    field={f}
                    value={record ? record[f.APIName] : null}
                    record={record}
                    recordID={record?._id || record?.id}
                    moduleAPIName={currentModule?.APIName}
                    table
                    reusableViewReferenceFields={reusableViews?.ReferenceFields}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
